/* assets/ctr-28-carousel.js
   <ctr28-carousel> — lightweight scroll-snap carousel for the EN-28 landing page.
   Markup:
     <ctr28-carousel>
       <div data-ctr28-track> ... <div data-ctr28-slide>…</div> ... </div>
       <div data-ctr28-dots></div>              (optional, dots are generated)
       <button data-ctr28-prev></button>        (optional)
       <button data-ctr28-next></button>        (optional)
     </ctr28-carousel>
   Works only while the track is actually scrollable (CSS decides the breakpoint),
   so the same markup can be a grid on desktop and a carousel on mobile. */
(function () {
  'use strict';
  if (customElements.get('ctr28-carousel')) return;

  class Ctr28Carousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ctr28-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-ctr28-slide]'));
      this.dotsWrap = this.querySelector('[data-ctr28-dots]');
      this.prevBtn = this.querySelector('[data-ctr28-prev]');
      this.nextBtn = this.querySelector('[data-ctr28-next]');
      this.label = this.dataset.slideLabel || 'Go to slide';

      this.buildDots();
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.update);
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1));
      this.index = 0;
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.update);
    }

    buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ctr28-carousel__dot';
        dot.setAttribute('aria-label', this.label + ' ' + (i + 1));
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    isActive() {
      return this.track.scrollWidth - this.track.clientWidth > 1;
    }

    currentIndex() {
      const start = this.track.getBoundingClientRect().left + parseFloat(getComputedStyle(this.track).scrollPaddingLeft || 0);
      let best = 0;
      let bestDist = Infinity;
      this.slides.forEach((slide, i) => {
        const dist = Math.abs(slide.getBoundingClientRect().left - start);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      // At the very end the last slides may never reach the start edge.
      if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2) best = this.slides.length - 1;
      return best;
    }

    goTo(i) {
      const index = Math.max(0, Math.min(this.slides.length - 1, i));
      const slide = this.slides[index];
      if (!slide) return;
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft || 0);
      const left = slide.offsetLeft - this.track.offsetLeft - padding;
      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    }

    update() {
      const active = this.isActive();
      this.toggleAttribute('data-active', active);
      if (!active) return;
      this.index = this.currentIndex();
      if (this.dots) {
        this.dots.forEach((dot, i) => {
          const current = i === this.index;
          dot.classList.toggle('is-active', current);
          if (current) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
    }
  }

  customElements.define('ctr28-carousel', Ctr28Carousel);
})();
